import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { RotateCcw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDateTime, formatNumber } from '../../../components/design-system/admin';
import { JobStatusBadge } from './JobStatusBadge';
import type { QueueType } from './queue-monitor.types';

interface JobDetailsSheetProps {
    open: boolean;
    jobId: string | null;
    /** The raw job document from `GET …/admin/job/:jobId`; null when it failed to load. */
    details: any | null;
    loading: boolean;
    queue: QueueType;
    replaying: boolean;
    onReplay: () => void;
    onClose: () => void;
}

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
    <div className="flex min-w-0 flex-col gap-1">
        <dt className="text-xs text-muted-foreground">{label}</dt>
        <dd className="truncate text-sm">{children}</dd>
    </div>
);

/** One job, everything the API knows about it, and a way to run it again. */
export function JobDetailsSheet({ open, jobId, details, loading, queue, replaying, onReplay, onClose }: JobDetailsSheetProps) {
    const { t } = useTranslation();
    const seamless = queue === 'seamless';
    const error = details?.errorDetails?.userMessage || details?.error;

    return (
        <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
            <SheetContent className="gap-0 data-[side=right]:sm:max-w-xl" showCloseButton={false} data-testid="job-details">
                <SheetHeader className="border-b">
                    <SheetTitle className="text-base">{t('ADMIN_QUEUE.DETAILS.TITLE')}</SheetTitle>
                    <SheetDescription className="truncate font-mono">{jobId}</SheetDescription>
                </SheetHeader>

                <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto p-4">
                    {loading ? (
                        <>
                            <Skeleton className="h-12 w-full" />
                            <Skeleton className="h-64 w-full" />
                        </>
                    ) : details ? (
                        <>
                            <dl className="grid grid-cols-2 gap-4">
                                <Field label={t('ADMIN_QUEUE.DETAILS.STATUS')}>
                                    <JobStatusBadge status={details.status} />
                                </Field>
                                <Field label={seamless ? t('ADMIN_QUEUE.DETAILS.PATTERN') : t('ADMIN_QUEUE.DETAILS.OPERATION')}>
                                    <span className="capitalize">{(seamless ? details.pattern : details.operation) || '—'}</span>
                                </Field>
                                <Field label={t('ADMIN_QUEUE.DETAILS.ATTEMPTS')}>
                                    <span className="tabular-nums">{formatNumber(details.attempts || 0)}</span>
                                </Field>
                                <Field label={t('ADMIN_QUEUE.DETAILS.CREATED')}>
                                    {details.createdAt ? formatDateTime(details.createdAt) : '—'}
                                </Field>
                            </dl>

                            {(details.error || details.errorDetails) && (
                                <div className="flex flex-col gap-1.5">
                                    <p className="text-xs text-destructive">{t('ADMIN_QUEUE.DETAILS.ERROR')}</p>
                                    <div className="rounded-lg p-3 ring-1 ring-destructive/30">
                                        <p className="font-mono text-xs break-words text-destructive">{error}</p>
                                        {details.errorDetails?.technicalDetails && (
                                            <p className="mt-2 font-mono text-xs break-words text-muted-foreground">
                                                {details.errorDetails.technicalDetails}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="flex min-h-0 flex-col gap-1.5">
                                <p className="text-xs text-muted-foreground">{t('ADMIN_QUEUE.DETAILS.FULL_DATA')}</p>
                                <pre
                                    className="max-h-[55vh] overflow-auto rounded-lg bg-muted p-3 font-mono text-xs break-words whitespace-pre-wrap"
                                    data-testid="job-details-json"
                                >
                                    {JSON.stringify(details, null, 2)}
                                </pre>
                            </div>
                        </>
                    ) : (
                        <p className="py-12 text-center text-sm text-muted-foreground">{t('ADMIN_QUEUE.DETAILS.LOAD_FAILED')}</p>
                    )}
                </div>

                <SheetFooter className="flex-row justify-end border-t">
                    <Button variant="outline" onClick={onClose}>
                        {t('ADMIN_QUEUE.DETAILS.CLOSE')}
                    </Button>
                    {details && !loading && (
                        <Button onClick={onReplay} disabled={replaying}>
                            <RotateCcw />
                            {t('ADMIN_QUEUE.DETAILS.REPLAY')}
                        </Button>
                    )}
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
}
